import {computed} from 'vue';
import {useRoute} from '@typed-router';
import {storeToRefs} from 'pinia';
import {useRequestURL, useState} from '#imports';
import {useFeatureFlag} from '@malt/feature-flags';
import {funnelPages, PAGES, STEPS, STEPS_USER_DETAILS_INVERTED_WITH_COMPANY_CHOICE, type FunnelPage, type FunnelStep} from '~/lib/steps';
import {useContextStore} from '~/store/context.store';
import {usePageSourceStore} from '~/store/page-source.store';
import {useSignupStore} from '~/store/signup.store';
import {useUserQualificationStore} from '~/store/user-qualification.store';

export function useFunnelSteps() {
    // Computed
    const route = useRoute();

    // Stores
    const contextStore = useContextStore();
    const {companyName, redirectPath, transitionalCompanyId} = storeToRefs(contextStore);
    const userQualificationStore = useUserQualificationStore();
    const {currentQuestionAnswered, currentQuestionIsFirst} = storeToRefs(userQualificationStore);
    const {addPageSourceParamToUrl} = usePageSourceStore();
    const signupStore = useSignupStore();
    const {emailValidity, emailModel} = storeToRefs(signupStore);
    const isUserDetailsInvertedWithCompanyChoice = useFeatureFlag('signup-client-invert-user-details-and-choose-company-steps');

    // Computed values
    /**
     * TODO extract this content in a map, then using this computed, get the expected value
     */
    const currentPage = computed((): FunnelPage | undefined => {
        const pagesFilteredByRoutePath = funnelPages.filter((page) => page.path === route.path);

        return pagesFilteredByRoutePath.length === 1 ? pagesFilteredByRoutePath[0] : undefined;
    });
    const currentStep = computed((): FunnelStep | undefined => {
        if (isUserDetailsInvertedWithCompanyChoice.value) {
            return STEPS_USER_DETAILS_INVERTED_WITH_COMPANY_CHOICE.find(({paths}) => paths.includes(route.path));
        }
        return STEPS.find(({paths}) => paths.includes(route.path));
    });
    const currentSubStepIndex = computed<number>(() => (currentStep.value?.paths.indexOf(route.path) ?? 0) + 1);

    const isAiBannerVisible = computed(() => route.path === PAGES.PUBLIC_EMAIL_WARNING);
    const isNotMyTeamButtonVisible = computed(() => currentPage.value?.path === PAGES.COMPANY_CHOICE && transitionalCompanyId.value);
    const isBackCtaVisible = computed(
        () =>
            [PAGES.EMAIL, PAGES.PASSWORD, PAGES.PUBLIC_EMAIL_WARNING].includes(route.path) ||
            (PAGES.USER_QUALIFICATION === route.path && !currentQuestionIsFirst.value),
    );
    const isCompanyVisible = computed(
        () => [PAGES.USER_DETAILS, PAGES.COMPANY_DETAILS, PAGES.USER_QUALIFICATION].includes(route.path) && companyName.value,
    );
    const isFunnelWithoutPadding = computed(() => route.path === PAGES.COMPANY_CHOICE);
    const isLoginCtaVisible = computed(() => route.path === PAGES.EMAIL);

    const isCompanySelected = useState('company-selected');

    const isNextCtaDisabled = computed(() => {
        if (route.path === PAGES.COMPANY_CHOICE && !isCompanySelected.value) {
            return true;
        }

        if (route.path === PAGES.USER_QUALIFICATION && !currentQuestionAnswered.value) {
            return true;
        }
        if (route.path === PAGES.EMAIL && (!emailValidity.value || !['VALID_WORK_EMAIL', 'VALID_NON_WORK_EMAIL'].includes(emailValidity.value))) {
            return true;
        }

        return false;
    });
    const isSkipCtaVisible = computed(() => [PAGES.USER_QUALIFICATION].includes(route.path));
    const signinHref = computed(() => {
        let url = _withCurrentRedirectParam('/signin');
        if (emailModel.value) {
            url += `${url.includes('?') ? '&' : '?'}email=${encodeURIComponent(emailModel.value)}`;
        }
        return addPageSourceParamToUrl(url);
    });
    const subSteps = computed<number>(() => currentStep.value?.paths.length ?? 1);

    function _withCurrentRedirectParam(url: string) {
        const currentUrl = useRequestURL();

        const redirectParam = redirectPath.value ?? currentUrl.searchParams.get('redirect');
        if (redirectParam) {
            return `${url}${url.includes('?') ? '&' : '?'}redirect=${encodeURIComponent(redirectParam)}`;
        }

        return url;
    }

    return {
        currentPage,
        currentStep,
        currentSubStepIndex,
        isAiBannerVisible,
        isBackCtaVisible,
        isNotMyTeamButtonVisible,
        isCompanyVisible,
        isFunnelWithoutPadding,
        isLoginCtaVisible,
        isNextCtaDisabled,
        isSkipCtaVisible,
        signinHref,
        subSteps,
    };
}
